/**
 * 模块: D3图表渲染逻辑
 * 职责: 创建和渲染力导向图
 * 限制: 文件<280行, 函数<30行
 */

import * as d3 from 'd3';
import { Node, Edge } from './types';
import { 
  FORCE_CONFIG, 
  NODE_CONFIG,
  createColorScale, 
  getNodeRadius, 
  getFontSize, 
  getTextColor, 
  getTextShadow,
  getEdgeColor,
  getEdgeWidth,
  EDGE_CONFIG
} from './config';
import { addSVGDefs } from './svg-defs';
import { createDragBehavior, addHoverInteractions } from './interactions';

/**
 * 渲染边/连线
 */
function renderEdges(
  svg: d3.Selection<SVGSVGElement, unknown, null, undefined>,
  edges: Edge[]
) {
  const link = svg.append('g')
    .selectAll('line')
    .data(edges)
    .join('line')
    .attr('stroke', (d: Edge) => getEdgeColor(d.type))
    .attr('stroke-opacity', EDGE_CONFIG.opacity)
    .attr('stroke-width', (d: Edge) => getEdgeWidth(d.weight))
    .attr('stroke-linecap', 'round');

  // 添加工具提示
  link.append('title')
    .text((d: Edge) => {
      const typeMap: Record<string, string> = {
        direct: '直接竞争',
        indirect: '间接竞争',
      };
      const typeText = typeMap[d.type] || '潜在竞争';
      const sourceId = typeof d.source === 'object' ? d.source.id : d.source;
      const targetId = typeof d.target === 'object' ? d.target.id : d.target;
      const keywords = Array.isArray(d.shared_keywords) ? d.shared_keywords.join(', ') : '';
      return `${sourceId} ↔ ${targetId}\n竞争频率: ${d.weight}\n竞争类型: ${typeText}${keywords ? `\n共同关键词: ${keywords}` : ''}`;
    });

  return link;
}

/**
 * 渲染节点
 */
function renderNodes(
  svg: d3.Selection<SVGSVGElement, unknown, null, undefined>,
  nodes: Node[],
  colorScale: d3.ScaleLinear<string, string>,
  simulation: d3.Simulation<any, undefined>
): d3.Selection<SVGCircleElement, Node, SVGGElement, unknown> {
  const node = svg.append('g')
    .selectAll<SVGCircleElement, Node>('circle')
    .data(nodes)
    .join('circle')
    .attr('r', (d: Node) => getNodeRadius(d.influence, d.label))
    .attr('fill', (d: Node) => colorScale(d.influence))
    .attr('stroke', '#ffffff')
    .attr('stroke-width', (d: Node) => NODE_CONFIG.strokeWidth)
    .attr('filter', 'url(#node-shadow)')
    .attr('opacity', 0.9)
    .style('cursor', 'pointer')
    .style('transition', 'all 0.3s ease')
    .call(createDragBehavior(simulation));

  // 添加工具提示
  node.append('title')
    .text((d: Node) => 
      `${d.label}\n影响力: ${d.influence.toFixed(1)}\n市场份额: ${d.marketShare.toFixed(1)}%`
    );

  return node;
}

/**
 * 渲染标签
 */
function renderLabels(
  svg: d3.Selection<SVGSVGElement, unknown, null, undefined>,
  nodes: Node[],
  colorScale: d3.ScaleLinear<string, string>,
  influenceThreshold: number
): d3.Selection<SVGTextElement, Node, SVGGElement, unknown> {
  const label = svg.append('g')
    .selectAll<SVGTextElement, Node>('text')
    .data(nodes)
    .join('text')
    .text((d: Node) => d.label)
    .attr('font-size', (d: Node) => {
      const radius = getNodeRadius(d.influence, d.label);
      const textLength = d.label ? d.label.length : 0;
      return getFontSize(radius, textLength);
    })
    .attr('font-weight', (d: Node) => 
      d.influence >= influenceThreshold ? 'bold' : '600'
    )
    .attr('fill', (d: Node) => getTextColor(colorScale(d.influence)))
    .attr('text-anchor', 'middle')
    .attr('dominant-baseline', 'central')
    .style('pointer-events', 'none')
    .style('text-shadow', (d: Node) => getTextShadow(colorScale(d.influence)))
    .style('user-select', 'none');

  return label;
}

/**
 * 主渲染函数
 */
export function renderGraph(
  svgElement: SVGSVGElement,
  nodes: Node[],
  edges: Edge[],
  width: number,
  height: number
) {
  // 清空之前的内容
  d3.select(svgElement).selectAll('*').remove();

  const svg = d3.select(svgElement)
    .attr('width', width)
    .attr('height', height)
    .attr('style', 'background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)');

  // 添加SVG定义
  addSVGDefs(svg);

  // 创建力导向图模拟
  const simulation = d3.forceSimulation(nodes as any)
    .force('link', d3.forceLink(edges).id((d: any) => d.id).distance(FORCE_CONFIG.linkDistance))
    .force('charge', d3.forceManyBody().strength(FORCE_CONFIG.chargeStrength))
    .force('center', d3.forceCenter(width / 2, height / 2))
    .force('collision', d3.forceCollide().radius(FORCE_CONFIG.collisionRadius))
    .alphaDecay(FORCE_CONFIG.alphaDecay)
    .velocityDecay(FORCE_CONFIG.velocityDecay);

  // 创建颜色比例尺
  const maxInfluence = d3.max(nodes, (d: Node) => d.influence) || 100;
  const colorScale = createColorScale(maxInfluence);

  // 计算影响力阈值
  const influenceThreshold = d3.quantile(
    nodes.map((d: Node) => d.influence).sort(d3.ascending), 
    0.7
  ) || 10;

  // 渲染元素
  const link = renderEdges(svg, edges);
  const node = renderNodes(svg, nodes, colorScale, simulation);
  const label = renderLabels(svg, nodes, colorScale, influenceThreshold);

  // 添加交互
  addHoverInteractions(node, label, influenceThreshold);

  // 更新位置（带边界限制）
  simulation.on('tick', () => {
    // 应用边界限制（增加更大的边距）
    nodes.forEach((d: any) => {
      const radius = getNodeRadius(d.influence, d.label);
      const padding = 30; // 增加边距：10 -> 30
      d.x = Math.max(radius + padding, Math.min(width - radius - padding, d.x));
      d.y = Math.max(radius + padding, Math.min(height - radius - padding, d.y));
    });

    link
      .attr('x1', (d: any) => d.source.x)
      .attr('y1', (d: any) => d.source.y)
      .attr('x2', (d: any) => d.target.x)
      .attr('y2', (d: any) => d.target.y);

    node
      .attr('cx', (d: any) => d.x)
      .attr('cy', (d: any) => d.y);

    label
      .attr('x', (d: any) => d.x)
      .attr('y', (d: any) => d.y);
  });

  return simulation;
}

